import { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faClose
} from '@fortawesome/free-solid-svg-icons';

import { COMMITMENT_TYPES } from "../../../constants/COMMITMENTS";

const Events = ({ types, commitments = [] }) => {
  const [items, setItems] = useState(commitments);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  // Sincroniza o estado interno caso a prop externa mude
  useEffect(() => {
    setItems(commitments);
  }, [commitments]);

  // Cálculos de paginação
  const totalPages = Math.ceil(items.length / itemsPerPage) || 1;
  const firstIndex = (currentPage - 1) * itemsPerPage;
  const lastIndex = firstIndex + itemsPerPage;

  const currentItems = useMemo(() => {
    return items.slice(firstIndex, lastIndex);
  }, [items, firstIndex, lastIndex]);

  // Lógica para gerar os números das páginas resumidos (Padrão Portais)
  const getPageNumbers = () => {
    const delta = 1; // Quantidade de páginas adjacentes à página atual
    const range = [];
    const rangeWithDots = [];

    // Sempre inclui a primeira, a última e os vizinhos da página atual
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    let previousPage;
    for (let page of range) {
      if (previousPage) {
        if (page - previousPage === 2) {
          rangeWithDots.push(previousPage + 1);
        } else if (page - previousPage > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(page);
      previousPage = page;
    }

    return rangeWithDots;
  };

  // Exclusão com ajuste correto de página
  function handleDelete(id) {
    if (!window.confirm("Deseja realmente excluir este registro?")) {
      return;
    }

    const updatedItems = items.filter((item) => item.id !== id);
    setItems(updatedItems);

    const newTotalPages = Math.ceil(updatedItems.length / itemsPerPage) || 1;
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages);
    }
  }

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  }

  function handleItemsPerPageChange(e) {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }

  const renderEventIcon = (commitment) => {
    console.warn()
    return (
      <FontAwesomeIcon
        icon={COMMITMENT_TYPES[commitment.commitment_type_id].icon}
        style={{ color: COMMITMENT_TYPES[commitment.commitment_type_id].color }}
      />
    );
  };

  const formatDateBR = (dateValue) => {
    if (!dateValue) return "";

    // Garante que o valor seja convertido para um objeto Date válido
    const date = new Date(dateValue);

    // Trata casos em que a string enviada não é uma data válida
    if (isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC" // Evita alterações indesejadas de fuso horário em strings 'AAAA-MM-DD'
    }).format(date);
  };

  // ---------------------------------------------------------------------
  return (
    <div className="card mx-3 mb-2">
      <div className="card-header bg-transparent">
        <strong>Próximos compromissos</strong>
      </div>

      <div className="card-body p-0">
        <div className="list-group list-group-flush p-1">
          {currentItems.length === 0 ? (
            <div className="p-3 text-muted">Nenhum compromisso encontrado.</div>
          ) : (
            currentItems.map((commitment) => (
              <div
                className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                key={commitment.id ?? `${commitment.date}-${commitment.title}`}
              >
                <div className="row align-items-center">
                  <div className="col-2">
                    <div className="fw-bold"><small>{formatDateBR(commitment.date)}</small></div>
                  </div>

                  {commitment.reference_type && (
                    <div className="col-1 text-end">{renderEventIcon(commitment)}</div>
                  )}

                  <div className="col">
                    <div className="fw-semibold">{commitment.title}</div>
                    <div className="small text-muted">
                      {commitment.description}
                    </div>
                  </div>

                  <div className="col-2 text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleDelete(commitment.id)}
                      title="Excluir compromisso"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    &nbsp;
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(commitment.id)}
                      title="Excluir compromisso"
                    >
                      <FontAwesomeIcon icon={faClose} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Rodapé da tabela / Paginação */}
        <div className="p-3 border-top">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">

            {/* Controles de Paginação com Padrão Bootstrap */}
            <nav aria-label="Navegação de páginas">
              <ul className="pagination pagination-sm m-0">

                {/* Botão Primeira Página */}
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(1)}
                    title="Primeira página"
                  >
                    &laquo;
                  </button>
                </li>

                {/* Botão Anterior */}
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    Anterior
                  </button>
                </li>

                {/* Lista de Páginas Resumidas */}
                {getPageNumbers().map((page, index) => {
                  if (page === "...") {
                    return (
                      <li key={`dots-${index}`} className="page-item disabled">
                        <span className="page-link">&hellip;</span>
                      </li>
                    );
                  }

                  return (
                    <li
                      key={page}
                      className={`page-item ${page === currentPage ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}

                {/* Botão Próxima */}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    Próxima
                  </button>
                </li>

                {/* Botão Última Página */}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => goToPage(totalPages)}
                    title="Última página"
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>

            {/* Select e Contador de Registros */}
            <div className="d-flex align-items-center gap-3 text-muted small ms-auto">
              {/* <label className="d-flex align-items-center gap-2 m-0">
                <span>Registros por página:</span>
                <select
                  className="form-select form-select-sm w-auto"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                </select>
              </label> */}

              <div>
                Página <strong>{currentPage}</strong> de{" "}
                <strong>{totalPages}</strong> — Total: {items.length}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;